// File: packages/chatbot/src/tools/frontendActions.ts
// Strict allowlist + normalization for in-app actions.

export type FrontendAction =
  | { name: 'select_preset'; params: { id: string } }
  | { name: 'toggle_feature'; params: { id: string; enabled: boolean } }
  | { name: 'open_section'; params: { id: string } }
  | { name: 'navigate'; params: { path: string } }
  | { name: 'set_copy'; params: { field: 'heroTitle' | 'heroSubtitle' | 'bullets' | 'integrations'; index?: number; value: string } }
  | { name: 'generate_quote'; params: {} }
  | { name: 'fill_field'; params: { formId: string; fieldId: string; value: string } }
  | { name: 'submit_lead_email'; params: { email: string } };

export interface FrontendToolContext {
  selectPreset: (id: string) => void;
  setFeature: (id: string, enabled: boolean) => void;
  openSection: (id: string) => void;
  safeNavigate: (path: string) => void;
  setPersonalizedCopy: (field: string, value: string, index?: number) => void;
  openQuoteWithCurrentConfig: () => Promise<{ url: string } | { error: string; code?: string }>;
  submitLeadEmail: (email: string) => Promise<{ ok: true; lead_id?: string } | { ok: false; error: string; code?: string }>;
  fillFormField: (formId: string, fieldId: string, value: string) => void;
}

type RunResult = {
  status: 'success' | 'error';
  executed: string[];
  count: number;
  results: Array<{ name: FrontendAction['name']; result: any }>;
  message?: string;
};

// Normalize various shapes into { actions: FrontendAction[] }
export function normalizeFrontendActionsArgs(raw: any): { actions: FrontendAction[] } {
  const normalized: FrontendAction[] = [];
  if (!raw || typeof raw !== 'object') return { actions: normalized };

  const aliasName = (name: string): FrontendAction['name'] | null => {
    const n = (name || '').toLowerCase();
    if (['navigate', 'go_to', 'goto', 'open_page'].includes(n)) return 'navigate';
    if (['open_section', 'opensection', 'open', 'scroll_to', 'scrollto'].includes(n)) return 'open_section';
    if (['select_preset', 'selectpreset', 'preset'].includes(n)) return 'select_preset';
    if (['toggle_feature', 'togglefeature'].includes(n)) return 'toggle_feature';
    if (['set_copy', 'setcopy'].includes(n)) return 'set_copy';
    if (['generate_quote', 'generatequote'].includes(n)) return 'generate_quote';
    if (['submit_lead_email', 'submitleademail'].includes(n)) return 'submit_lead_email';
    if (['fill_field', 'fillfield'].includes(n)) return 'fill_field';
    return null;
  };

  const pushAction = (name: string, params: any) => {
    const aliased = aliasName(name);
    if (!aliased) return;

    if (aliased === 'navigate') {
      const p = typeof params === 'string' ? params : params?.path;
      if (typeof p === 'string' && p.trim()) normalized.push({ name: 'navigate', params: { path: p } });
      return;
    }
    if (aliased === 'open_section') {
      let id = typeof params === 'string' ? params : params?.id;
      if (typeof id === 'string' && id.startsWith('#')) id = id.slice(1);
      if (typeof id === 'string' && id.trim()) normalized.push({ name: 'open_section', params: { id } });
      return;
    }
    if (aliased === 'select_preset') {
      const id = typeof params === 'string' ? params : params?.id;
      if (typeof id === 'string' && id.trim()) normalized.push({ name: 'select_preset', params: { id } });
      return;
    }
    if (aliased === 'toggle_feature') {
      const id = params?.id;
      const enabled = typeof params?.enabled === 'boolean' ? params.enabled : Boolean(params?.on ?? params?.value);
      if (typeof id === 'string' && id.trim()) normalized.push({ name: 'toggle_feature', params: { id, enabled } });
      return;
    }
    if (aliased === 'set_copy') {
      const { field, value, index } = params || {};
      if (typeof field === 'string' && typeof value === 'string' && ['heroTitle', 'heroSubtitle', 'bullets', 'integrations'].includes(field)) {
        normalized.push({ name: 'set_copy', params: { field: field as any, value, index } });
      }
      return;
    }
    if (aliased === 'generate_quote') {
      normalized.push({ name: 'generate_quote', params: {} });
      return;
    }
    if (aliased === 'submit_lead_email') {
      const email = typeof params === 'string' ? params : params?.email;
      if (typeof email === 'string' && email.trim()) normalized.push({ name: 'submit_lead_email', params: { email } });
      return;
    }
    if (aliased === 'fill_field') {
      const { formId, fieldId, value } = params || {};
      if (typeof formId === 'string' && typeof fieldId === 'string') normalized.push({ name: 'fill_field', params: { formId, fieldId, value: String(value ?? '') } });
      return;
    }
  };

  if (Array.isArray(raw.actions)) {
    for (const a of raw.actions) {
      if (!a) continue;
      const name = a.name || a.action;
      pushAction(name, a.params ?? a.args ?? a);
    }
    return { actions: normalized };
  }

  if (Array.isArray(raw.steps)) {
    for (const step of raw.steps) {
      const name = step?.name || step?.action;
      pushAction(name, step?.params ?? step?.args ?? step);
    }
    return { actions: normalized };
  }

  if (typeof raw.go_to === 'string') pushAction('navigate', { path: raw.go_to });
  if (typeof raw.open === 'string') pushAction('open_section', { id: raw.open });
  if (typeof raw.navigate === 'string') pushAction('navigate', { path: raw.navigate });
  if (raw.action) pushAction(raw.action, raw.params ?? raw.args ?? {});

  for (const [k, v] of Object.entries(raw)) {
    if (['actions', 'steps', 'plan', 'args', 'params', 'tool_name'].includes(k)) continue;
    const aliased = aliasName(k);
    if (aliased) pushAction(aliased, v);
  }

  return { actions: normalized };
}

export async function runFrontendActions(
  args: { actions?: FrontendAction[] } | any,
  ctx: FrontendToolContext,
  allowed?: ReadonlyArray<FrontendAction['name']>
): Promise<RunResult> {
  const normalized = normalizeFrontendActionsArgs(args);
  if (!normalized.actions || normalized.actions.length === 0) {
    return { status: 'error', executed: [], count: 0, results: [], message: 'No recognizable actions provided.' };
  }

  const executed: string[] = [];
  const results: RunResult['results'] = [];

  try {
    for (const action of normalized.actions) {
      if (allowed && !allowed.includes(action.name)) {
        results.push({ name: action.name, result: { status: 'error', code: 'not_allowed' } });
        continue;
      }

      switch (action.name) {
        case 'select_preset':
          ctx.selectPreset(action.params.id);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        case 'toggle_feature':
          ctx.setFeature(action.params.id, action.params.enabled);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        case 'open_section':
          ctx.openSection(action.params.id);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        case 'navigate':
          ctx.safeNavigate(action.params.path);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        case 'set_copy':
          ctx.setPersonalizedCopy(action.params.field, action.params.value, action.params.index);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        case 'generate_quote': {
          const r = await ctx.openQuoteWithCurrentConfig();
          results.push({ name: action.name, result: r });
          break;
        }
        case 'submit_lead_email': {
          const r = await ctx.submitLeadEmail(action.params.email);
          results.push({ name: action.name, result: r });
          break;
        }
        case 'fill_field':
          ctx.fillFormField(action.params.formId, action.params.fieldId, action.params.value);
          results.push({ name: action.name, result: { status: 'success' } });
          break;
        default:
          // Unknown action
          // eslint-disable-next-line no-console
          console.warn('Attempted to run unknown frontend action:', action);
          continue;
      }
      executed.push(action.name);
    }
    return { status: 'success', executed, count: executed.length, results };
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.error('Error executing frontend actions:', error);
    return { status: 'error', executed, count: executed.length, results, message: error?.message || 'Unexpected error.' };
  }
}
